import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

const useStyles = makeStyles((theme) => ({
  popupContainer: {
    minWidth: '200px',
    maxWidth: '400px',
    padding: theme.spacing(1),
  },
  featureSection: {
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  layerTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  propertyRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  propertyLabel: {
    fontWeight: 500,
    marginRight: theme.spacing(1),
    minWidth: '100px',
  },
  propertyValue: {
    flex: 1,
    wordBreak: 'break-word',
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
}));

/**
 * Renders feature information based on a template
 * @param {Object} feature - The feature object with properties
 * @param {Object|Function} template - Template can be:
 *   - Object: { title: 'string or function', fields: [{label: 'Label', key: 'propertyKey', format: function}] }
 *   - Function: (properties) => JSX element
 */
const renderFeatureContent = (feature, template, classes) => {
  const properties = feature.properties || {};

  // If template is a function, call it with properties
  if (typeof template === 'function') {
    return template(properties, feature);
  }

  // If template is an object with fields
  if (template && typeof template === 'object') {
    const title = typeof template.title === 'function' 
      ? template.title(properties) 
      : template.title || 'Feature';

    return (
      <div>
        <Typography variant="subtitle2" className={classes.layerTitle}>
          {title}
        </Typography>
        {template.fields && template.fields.map((field, idx) => {
          const value = properties[field.key];
          const formattedValue = field.format 
            ? field.format(value, properties) 
            : value;

          // Skip if value is undefined/null and skipEmpty is true
          if ((value === undefined || value === null) && field.skipEmpty) {
            return null;
          }

          return (
            <div key={idx} className={classes.propertyRow}>
              <span className={classes.propertyLabel}>{field.label}:</span>
              <span className={classes.propertyValue}>
                {formattedValue !== undefined && formattedValue !== null 
                  ? String(formattedValue) 
                  : '-'}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Default: show all properties
  return (
    <div>
      <Typography variant="subtitle2" className={classes.layerTitle}>
        Feature
      </Typography>
      {Object.entries(properties).map(([key, value]) => (
        <div key={key} className={classes.propertyRow}>
          <span className={classes.propertyLabel}>{key}:</span>
          <span className={classes.propertyValue}>
            {value !== undefined && value !== null ? String(value) : '-'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function GenericFeaturePopup({ features, layers }) {
  const classes = useStyles();

  if (!features || features.length === 0) {
    return (
      <div className={classes.popupContainer}>
        <Typography variant="body2">No features found</Typography>
      </div>
    );
  }

  return (
    <div className={classes.popupContainer}>
      {features.map((feature, idx) => {
        // Find the layer configuration for this feature
        const layerConfig = layers?.find(layer => {
          // Match by source or layer id
          return feature.source === layer.id || feature.layer?.id?.includes(layer.id);
        });

        const template = layerConfig?.popupTemplate;

        return (
          <div key={idx} className={classes.featureSection}>
            {idx > 0 && <Divider className={classes.divider} />}
            {renderFeatureContent(feature, template, classes)}
          </div>
        );
      })}
    </div>
  );
}
